import { z } from 'zod';
import { boardSchema } from './board';
import { boardBackgroundSchema } from './board-background';
import { ulidSchema } from './id';
import { WORKSPACE_NAME_MAX_LENGTH } from './workspace';

/**
 * The boards landing page — block **B23**, `FR-NOTIF-029` ("Boards landing
 * page"), `BE-23.1`, extended by **B48**, `FR-BOARD-004` and
 * `FR-SEARCH-016`.
 *
 * This file declares **no constant**. B23 introduces no column of its own —
 * every field it serves already has a home (`ulidSchema` for an id,
 * `WORKSPACE_NAME_MAX_LENGTH` for a workspace's name, `boardListSchema` for
 * the tiles themselves) — so `card.ts`'s rule ("import the constant
 * describing your column if one exists, declare it if none does") never
 * takes its "none does" branch here, exactly as `cross-board-move.ts` did
 * not.
 *
 * ---------------------------------------------------------------------------
 * `groups` IS A WRAPPER KEY, NOT SPECULATIVE ROOM FOR B48
 * ---------------------------------------------------------------------------
 * `GET /boards` and `GET /workspaces/:id/boards` are near-identically named
 * and return near-identical tiles. A bare `z.array(boardGroupSchema)` at this
 * route's top level would be structurally indistinguishable, at a glance,
 * from `boardListSchema` — the bare array of *boards* the other route
 * returns. A named key is self-describing at the one place that ambiguity
 * would actually bite: a client holding an untyped response body.
 *
 * B48 is that argued addition: `FR-NOTIF-029` names the section order
 * ("starred boards; recently viewed boards; then every board") and
 * `FR-SEARCH-016` names the same private board sets for the jump-to panel.
 * They reuse this route-specific board tile view, still without widening
 * `boardSchema` globally.
 *
 * ---------------------------------------------------------------------------
 * A GROUP IS A HEADING OVER BOARDS, NOT AN EMBEDDED WORKSPACE RESOURCE
 * ---------------------------------------------------------------------------
 * `workspaceId` and `workspaceName` are **flat** fields on `boardGroupSchema`,
 * deliberately not a nested `workspace: workspaceSchema`. Nesting the full
 * workspace view would invite a client to render the heading as a link into
 * the workspace, and `FR-PERM-023` forbids exactly that for a guest who can
 * see a single board in a workspace: "guests see only the board they were
 * added to — not the workspace board list, other boards, or the workspace
 * member list." `FR-NOTIF-029`'s own edge case says the same thing from this
 * surface's side — "a guest who can see a single board in a workspace sees
 * that board without gaining a full workspace grouping." A named heading is
 * not a grant of workspace visibility; a linked one would be.
 *
 * `shortName` is deliberately absent for the identical reason — it is "used
 * in the workspace URL" (`FR-PERM-005`), and serving it here would hand a
 * client exactly the material a linked heading needs. If a later block wants
 * linked headings for actual workspace members, that is an argued addition
 * made when it is wanted, not an omission tidied away by adding the field
 * now.
 *
 * ---------------------------------------------------------------------------
 * NOTHING ELSE ON A GROUP — NO COUNT, NO ROLE, NO `isMember`
 * ---------------------------------------------------------------------------
 * A `boardCount` computed over the workspace's *whole* board set (rather than
 * over the boards actually admitted into `boards` below) would tell a
 * single-board guest how many boards exist that they may not see —
 * `FR-PERM-023` violated by a number, with no board object attached to point
 * at. A member count or a viewer role would be "a full workspace grouping" by
 * another name, which is precisely what `FR-NOTIF-029`'s edge case withholds
 * from a guest. The safe shape is the one below: a group carries the
 * workspace's identity, its name, and the boards the caller may actually
 * list — nothing computed over rows the caller cannot see.
 *
 * ---------------------------------------------------------------------------
 * `boards` IS A BOARD TILE VIEW, NOT `boardSchema` WIDENED IN PLACE
 * ---------------------------------------------------------------------------
 * B23 originally returned `boardSchema` verbatim here. B42 adds the tile
 * background `FR-NOTIF-029` names, but still keeps `boardSchema` itself narrow:
 * that base schema serialises B08/B09/B10 point routes and `search.ts`'s board
 * group. The page route extends a copy so only this board-tile surface carries
 * `background`.
 */
export const boardPageBoardSchema = boardSchema.extend({
  background: boardBackgroundSchema,
  isTemplate: z.boolean(),
});

export type BoardPageBoard = z.infer<typeof boardPageBoardSchema>;

/**
 * `FR-SEARCH-016`'s "commonly cited as 8, verify" cap, settled by B48.
 *
 * The cap is exported so the server route, tests, and frontend can share the
 * same number rather than hand-maintaining a second literal.
 */
export const RECENT_BOARD_LIMIT = 8;

export const boardGroupSchema = z.object({
  /** `workspaces.id`. */
  workspaceId: ulidSchema,
  /** `workspaces.name`. */
  workspaceName: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  /** The boards this caller may list in this workspace. */
  boards: z.array(boardPageBoardSchema),
});

export type BoardGroup = z.infer<typeof boardGroupSchema>;

/**
 * `GET /boards?state=open|closed` — the whole response.
 *
 * The querystring is `boardListQuerySchema` from `./board`, reused verbatim
 * rather than redeclared here — see that schema's own docstring, amended by
 * this block to name both routes it now serves. There is deliberately no
 * second `{ state }` shape in this file.
 */
export const boardsPageSchema = z.object({
  /** `FR-BOARD-004` — this caller's private stars, in rank order. */
  starred: z.array(boardPageBoardSchema),
  /** `FR-SEARCH-016` — this caller's private recent boards, newest first. */
  recent: z.array(boardPageBoardSchema),
  groups: z.array(boardGroupSchema),
});

export type BoardsPage = z.infer<typeof boardsPageSchema>;

export const boardStarStateSchema = z.object({
  boardId: ulidSchema,
  starred: z.boolean(),
});

export type BoardStarState = z.infer<typeof boardStarStateSchema>;

export const reorderBoardStarBodySchema = z.strictObject({
  previousBoardId: ulidSchema.nullish().transform((value) => value ?? null),
  nextBoardId: ulidSchema.nullish().transform((value) => value ?? null),
});

export type ReorderBoardStarBody = z.infer<typeof reorderBoardStarBodySchema>;

export const boardRecentViewSchema = z.object({
  boardId: ulidSchema,
  viewedAt: z.string().datetime({ offset: true }),
});

export type BoardRecentView = z.infer<typeof boardRecentViewSchema>;

export const searchJumpToQuerySchema = z.strictObject({});

export type SearchJumpToQuery = z.infer<typeof searchJumpToQuerySchema>;

export const searchJumpToSchema = z.object({
  starred: z.array(boardPageBoardSchema),
  recent: z.array(boardPageBoardSchema),
});

export type SearchJumpTo = z.infer<typeof searchJumpToSchema>;
